/**
 * modelSettings — what each portfolio saves for its model portfolio (WORK.md
 * item 57, "What each portfolio saves" and "Where the tax state, the triggers
 * and the runs are saved"; the owner's grilling of 2026-10-24).
 *
 * Stored in vip-store's `settings` entry until item 55's `portfolios` table
 * comes, in two layers, each a PARTIAL block (every field optional):
 *   settings.modelDefaults                  — the person's default
 *   settings.portfolios[i].modelSettings    — that portfolio's own
 * A portfolio reads its own value of a field, else the person's, else the
 * built-in default below (`resolveModelSettings`). An old blob with neither
 * layer reads as the built-in defaults, so nothing needs a migration.
 *
 * Units: every rate, weight, gap and fee is a FRACTION (0.10 is 10%), as the
 * projection endpoints take them; the dealing charge is money in the
 * portfolio's base currency; the cooldown is whole days.
 *
 * One definition of the bounds, read three ways: the edge's settings write
 * guard (`modelSettingsProblem`, refusing a write with the bound named, never
 * clamping), the reader (`resolveModelSettings`, which leaves out a stored
 * field that breaks its bound and says so in `ignored`), and the app's zod
 * schema (app/src/lib/modelSettings.ts), built from the constants here and
 * held to this guard by test. This module stores and checks; it decides
 * nothing (ADR-0031): the solve is python-compute's `/projection-model`, the
 * tax the TypeScript tax modules'.
 */

/** The solver's aims (item 57, "The optimiser"). */
export const AIM_KINDS = ['max_return', 'min_vol', 'max_sharpe', 'target_vol', 'target_return'] as const;
export type AimKind = typeof AIM_KINDS[number];

/** Instrument types the buyable-universe filter names. */
export const INSTRUMENT_TYPES = ['stock', 'etf', 'fund', 'etn', 'bond'] as const;
export type InstrumentType = typeof INSTRUMENT_TYPES[number];

/**
 * How often the nightly model batch reviews a portfolio when nothing it reads
 * has changed (WORK.md item 57 step 6): every night, every week, or every 1,
 * 3, 6 or 12 months. A review is the scheduled re-check prices need; a trade,
 * a settings change or a closing 30-day window wakes the check on its own.
 * Daily by default, so every portfolio is checked every night (the owner's
 * decision 4 of 2026-10-31); the others stay for a portfolio that sets one.
 */
export const REVIEW_FREQUENCIES = ['daily', 'weekly', 'monthly', 'quarterly', 'half_yearly', 'yearly'] as const;
export type ReviewFrequency = typeof REVIEW_FREQUENCIES[number];

/**
 * The packaged products: a fund, an ETF or an ETN. The domicile exclusion,
 * UCITS and the "available funds" list judge these alone, so a filter that
 * leaves out US-domiciled ETFs still admits a US stock.
 */
export const PACKAGED_TYPES: readonly InstrumentType[] = ['etf', 'fund', 'etn'];

/** A closed interval [min, max], or (min, max] where `minExclusive`. */
export interface Bound { min: number; max: number; minExclusive?: boolean }

/** Every numeric bound, in the units the module docstring names. */
export const MODEL_BOUNDS = Object.freeze({
  modelVersion: { min: 1, max: 9999 },
  targetVol: { min: 0, max: 1, minExclusive: true },
  targetReturn: { min: -0.5, max: 1 },
  volCap: { min: 0, max: 1, minExclusive: true },
  returnFloor: { min: -0.5, max: 1 },
  maxDrawdown: { min: 0, max: 1, minExclusive: true },
  maxTurnover: { min: 0, max: 1, minExclusive: true },
  /** A group may go to 80% by default and be widened to 100%, never narrowed below (Q14). */
  groupMax: { min: 0.8, max: 1 },
  cryptoExpectedReturn: { min: -0.5, max: 1 },
  dealingCharge: { min: 0, max: 1000 },
  fxFee: { min: 0, max: 0.05 },
  platformFee: { min: 0, max: 0.05 },
  /** D21's bars; python-compute `ProjectionDriftRequest` takes the same units. */
  returnGap: { min: 0, max: 0.5 },
  confidence: { min: 0.5, max: 0.99 },
  volGap: { min: 0, max: 0.5, minExclusive: true },
  /** The minimum expected gain net of tax and costs, as a fraction of the book's value. */
  minNetGain: { min: 0, max: 0.2 },
  cooldownDays: { min: 0, max: 365 },
  /**
   * The review day: weekly, 1 (Monday) to 7 (Sunday); monthly and longer, the
   * day of the month, to 28 so every month has it. Null spreads the
   * portfolios by an offset of their id. Daily reads no day.
   */
  reviewDay: { min: 1, max: 28 },
  presetVersion: { min: 1, max: 9999 },
} satisfies Record<string, Bound>);

/** The default every group's band takes (Q14). */
export const GROUP_MAX_DEFAULT = 0.8;
/** The longest ticker a list may hold: python-compute's and the app's TICKER_MAX_LEN. */
export const MODEL_TICKER_MAX_LEN = 64;
/** The most tickers the frozen and no-sell lists hold: python-compute's `no_sell` max_length. */
export const MAX_HELD_LIST = 400;
/** The most codes a universe allow-list holds. */
export const MAX_UNIVERSE_LIST = 300;
/** The most funds an "available funds" list holds (a platform's or a pension's range). */
export const MAX_AVAILABLE_FUNDS = 1000;

export interface ModelRef { id: string; version: number }
export interface Aim { kind: AimKind; value: number | null }

/** The optional buyable-universe filter (Q20). A null list admits any value. */
export interface UniverseFilter {
  /** The residence preset the filter started from, or null for one built by hand. */
  preset: { id: string; version: number } | null;
  /** Listing countries (ISO 3166 alpha-2). */
  countries: string[] | null;
  /** Exchanges (the warehouse's exchange codes: LSE, XETRA, US). */
  exchanges: string[] | null;
  /** Domiciles any instrument may have (ISO 3166 alpha-2). */
  domiciles: string[] | null;
  /** Domiciles a fund, ETF or ETN may NOT have (the UK's PRIIPs rule leaves out US). */
  fundDomicilesExcluded: string[];
  types: InstrumentType[] | null;
  /** Trading currencies (ISO 4217). */
  currencies: string[] | null;
  /** 'required': a fund, ETF or ETN must be known to be UCITS. */
  ucits: 'any' | 'required';
  /** A platform's or a pension's own range: a fund, ETF or ETN only from here. */
  availableFunds: string[] | null;
}

export interface ModelSettings {
  /** The model and its version; null until one is chosen. */
  model: ModelRef | null;
  aim: Aim;
  /** Each limit is a constraint; null is no limit. */
  limits: { volCap: number | null; returnFloor: number | null; maxDrawdown: number | null; maxTurnover: number | null };
  /** Kept holdings, FROZEN: the solver neither buys nor sells them (Q56). */
  frozen: string[];
  /** Holdings the solver may buy and never sell. */
  noSell: string[];
  /** Off: the solve uses only what is held (B). On: it keeps the marked holdings and fills the rest (C) (Q2). */
  newHoldings: boolean;
  /** Each off by default (Q11, Q17). */
  suggestions: { stocks: boolean; funds: boolean; alpha: boolean };
  /** Crypto ETNs, left out unless allowed (Q33, Q44). A null return is the money-market rate, labelled. */
  crypto: { allowed: boolean; expectedReturn: number | null };
  /** The most each group may hold; above 0.8 the band is the user's, and labelled (Q14). */
  groupMax: { equity: number; bonds: number };
  costs: { dealingCharge: number; fxFee: number; platformFee: number };
  /** The trigger bars (D21's halves, the net gain, the cooldown, the AEA flag). */
  triggers: {
    returnGap: number; confidence: number; volGap: number;
    minNetGain: number; cooldownDays: number; aeaFlag: boolean;
  };
  /** The optional buyable-universe filter; null is no filter. */
  universe: UniverseFilter | null;
  /** When the nightly batch reviews the portfolio with nothing changed (item 57 step 6). */
  review: { frequency: ReviewFrequency; day: number | null };
}

/** The built-in default every portfolio starts from when no layer sets a field. */
export const MODEL_SETTINGS_DEFAULTS: ModelSettings = deepFreeze({
  model: null,
  aim: { kind: 'max_sharpe', value: null },
  limits: { volCap: null, returnFloor: null, maxDrawdown: null, maxTurnover: null },
  frozen: [],
  noSell: [],
  newHoldings: false,
  suggestions: { stocks: false, funds: false, alpha: false },
  crypto: { allowed: false, expectedReturn: null },
  groupMax: { equity: GROUP_MAX_DEFAULT, bonds: GROUP_MAX_DEFAULT },
  costs: { dealingCharge: 0, fxFee: 0, platformFee: 0 },
  triggers: { returnGap: 0.03, confidence: 0.7, volGap: 0.02, minNetGain: 0, cooldownDays: 30, aeaFlag: true },
  universe: null,
  review: { frequency: 'daily', day: null },
});

/** One residence preset: the owner's, versioned, never edited once shipped. */
export interface ResidencePreset {
  id: string;
  version: number;
  label: string;
  /** Why the preset is what it is, in the owner's words where they gave them. */
  note: string;
  filter: Omit<UniverseFilter, 'preset'>;
}

/**
 * The residence presets (Q20). The owner's: a change is a NEW version beside
 * the old, never an edit, so a portfolio that started from uk v1 still says
 * what it started from. Each is editable per portfolio once copied in.
 */
export const RESIDENCE_PRESETS: readonly ResidencePreset[] = deepFreeze([
  {
    id: 'uk',
    version: 1,
    label: 'United Kingdom',
    note: 'Leaves out US-domiciled funds, ETFs and ETNs, which publish no PRIIPs key information '
      + 'document and so are not sold to UK retail investors; foreign stocks stay buyable.',
    filter: {
      countries: null,
      exchanges: null,
      domiciles: null,
      fundDomicilesExcluded: ['US'],
      types: null,
      currencies: null,
      ucits: 'any',
      availableFunds: null,
    },
  },
]);

/** The preset with this id and version (the latest version when none is given), or null. */
export function findPreset(id: string, version?: number): ResidencePreset | null {
  const all = RESIDENCE_PRESETS.filter((p) => p.id === id);
  if (version !== undefined) return all.find((p) => p.version === version) ?? null;
  return all.reduce<ResidencePreset | null>((a, p) => (a && a.version >= p.version ? a : p), null);
}

/** A portfolio's filter copied from a preset, marked with where it came from. */
export function filterFromPreset(id: string, version?: number): UniverseFilter | null {
  const p = findPreset(id, version);
  if (!p) return null;
  return { preset: { id: p.id, version: p.version }, ...structuredClone(p.filter) as Omit<UniverseFilter, 'preset'> };
}

/** True when a filter that names its preset no longer equals it: the user's edit, to be said. */
export function presetEdited(filter: UniverseFilter): boolean {
  if (!filter.preset) return false;
  const p = findPreset(filter.preset.id, filter.preset.version);
  if (!p) return true;
  const { preset: _preset, ...rest } = filter;
  return canonical(rest) !== canonical(p.filter);
}

/** Which groups the user widened past the default band, to be labelled as theirs (Q14). */
export function widenedGroups(groupMax: ModelSettings['groupMax']): Array<'equity' | 'bonds'> {
  return (['equity', 'bonds'] as const).filter((g) => groupMax[g] > GROUP_MAX_DEFAULT);
}

/** What the universe filter knows of one instrument. Unknown is null, never a guess. */
export interface UniverseInstrument {
  code: string;
  type: InstrumentType | null;
  country: string | null;
  exchange: string | null;
  domicile: string | null;
  currency: string | null;
  /** True or false when known; null is unknown, never "not UCITS". */
  ucits: boolean | null;
}

/**
 * Whether the filter admits an instrument, and if not, why. Fails CLOSED: a
 * field the filter constrains and the instrument does not know is refused
 * (item 57, "Uncertain cases fail closed"). The one definition; the nightly
 * batch's Node half applies it to the candidates before the solve.
 */
export function universeAdmits(filter: UniverseFilter | null, inst: UniverseInstrument): { ok: true } | { ok: false; reason: string } {
  if (!filter) return { ok: true };
  const no = (reason: string) => ({ ok: false as const, reason });
  const inList = (list: string[] | null, v: string | null, what: string) => {
    if (list === null) return null;
    if (v === null) return `${what} unknown`;
    return list.includes(v) ? null : `${what} ${v} not in the filter`;
  };
  for (const why of [
    inList(filter.types, inst.type, 'type'),
    inList(filter.countries, inst.country, 'country'),
    inList(filter.exchanges, inst.exchange, 'exchange'),
    inList(filter.currencies, inst.currency, 'currency'),
    inList(filter.domiciles, inst.domicile, 'domicile'),
  ]) if (why) return no(why);
  const packaged = inst.type === null ? null : PACKAGED_TYPES.includes(inst.type);
  const packagedRule = filter.fundDomicilesExcluded.length > 0 || filter.ucits === 'required' || filter.availableFunds !== null;
  if (packagedRule && packaged === null) return no('type unknown, so whether it is a fund is unknown');
  if (!packaged) return { ok: true };
  if (filter.fundDomicilesExcluded.length > 0) {
    if (inst.domicile === null) return no('fund domicile unknown');
    if (filter.fundDomicilesExcluded.includes(inst.domicile)) return no(`fund domiciled in ${inst.domicile}`);
  }
  if (filter.ucits === 'required' && inst.ucits !== true) {
    return no(inst.ucits === null ? 'UCITS status unknown' : 'not UCITS');
  }
  if (filter.availableFunds !== null && !filter.availableFunds.includes(inst.code)) {
    return no('not in the available funds list');
  }
  return { ok: true };
}

/**
 * VIP's universe floors (WORK.md item 57, the owner's decision 9 of
 * 2026-10-31): a stock, ETF, ETN or fund is suggested only with at least
 * `historyYears` of price history and a median daily traded value of at
 * least `medianTradedValueGbp`, close x volume in GBP over the last
 * `sessions` sessions. They are VIP's universe whatever filter a portfolio
 * sets, so they are not a stored field and no preset carries them; a bond
 * keeps the rules it has. The two numbers are computed once, in Python
 * (projections/universe_floors.py, ADR-0031); this module only compares the
 * served values with the floors.
 */
export const UNIVERSE_FLOORS = Object.freeze({ historyYears: 3, medianTradedValueGbp: 1_000_000, sessions: 60 });

/** The types the floors judge; every other known type keeps its own rules. */
export const FLOORED_TYPES: readonly InstrumentType[] = ['stock', 'etf', 'etn', 'fund'];

/** projections/universe_floors.py `floor_facts`, as served; its keys held to this by test. */
export interface UniverseFloorFacts {
  /** The first published bar's date, or null with none. */
  firstDate: string | null;
  /** The median of close x volume in GBP over the last 60 sessions, or null when unreadable. */
  medianTradedValueGbp: number | null;
  /** How many sessions the median read. */
  sessions: number;
  /** Why a null is null; null when both were read. */
  reason: string | null;
}

/** One name the model may suggest or already holds, with what the floors read. */
export interface UniverseCandidate extends UniverseInstrument {
  /** In the book now: kept whatever the verdict, only never suggested past it. */
  held: boolean;
  /** Null when nothing was served for the name: it fails closed. */
  floors: UniverseFloorFacts | null;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** The same day `years` calendar years before a YYYY-MM-DD date; 29 February reads as the 28th. */
export function yearsBefore(date: string, years: number): string {
  if (!ISO_DATE.test(date)) throw new Error(`not a YYYY-MM-DD date: ${date}`);
  const [y, m, d] = date.split('-').map(Number);
  const lastDay = new Date(Date.UTC(y - years, m, 0)).getUTCDate();
  const pad = (n: number, w = 2) => String(n).padStart(w, '0');
  return `${pad(y - years, 4)}-${pad(m)}-${pad(Math.min(d, lastDay))}`;
}

/**
 * Whether the floors admit a name on `asOf`, and if not, why. Fails CLOSED:
 * a name of unknown type, with nothing served, or whose first date or traded
 * value could not be read is refused, with the reason Python gave.
 */
export function floorsAdmit(inst: UniverseCandidate, asOf: string): { ok: true } | { ok: false; reason: string } {
  const no = (reason: string) => ({ ok: false as const, reason });
  if (inst.type === null) return no('type unknown, so whether the floors apply is unknown');
  if (!FLOORED_TYPES.includes(inst.type)) return { ok: true };
  const f = inst.floors;
  if (!f) return no('price history and traded value not served');
  if (f.firstDate === null || !ISO_DATE.test(f.firstDate)) {
    return no(`price history unreadable: ${f.reason ?? `first date ${JSON.stringify(f.firstDate)}`}`);
  }
  const since = yearsBefore(asOf, UNIVERSE_FLOORS.historyYears);
  if (f.firstDate > since) {
    return no(`price history from ${f.firstDate}, under ${UNIVERSE_FLOORS.historyYears} years (needs ${since} or earlier)`);
  }
  const tv = f.medianTradedValueGbp;
  if (typeof tv !== 'number' || !Number.isFinite(tv)) {
    return no(`traded value unreadable: ${f.reason ?? 'not served'}`);
  }
  if (tv < UNIVERSE_FLOORS.medianTradedValueGbp) {
    return no(`median daily traded value £${Math.floor(tv).toLocaleString('en-GB')}, under £${UNIVERSE_FLOORS.medianTradedValueGbp.toLocaleString('en-GB')}`);
  }
  return { ok: true };
}

/** One name's place in the model's universe: suggestable, or not and why. */
export interface UniverseVerdict {
  code: string;
  suggestable: boolean;
  /** Carried from the candidate: a held name stays in the book whatever `suggestable` says. */
  held: boolean;
  /** The first rule that refused it, the filter's before the floors'; null when suggestable. */
  reason: string | null;
}

/**
 * The model's universe on `asOf`: one verdict for every candidate, in the
 * order given, so a name left out is named with its reason and never dropped.
 * A name is suggestable when the portfolio's filter (`universeAdmits`) and
 * the floors (`floorsAdmit`) both admit it. The verdict judges what may be
 * SUGGESTED: a held name the rules refuse is still the book's, and the caller
 * keeps it in the solve, holding or selling it, never buying more.
 */
export function modelUniverse(filter: UniverseFilter | null, candidates: UniverseCandidate[], asOf: string): UniverseVerdict[] {
  yearsBefore(asOf, 0);
  return candidates.map((c) => {
    const why = universeAdmits(filter, c);
    const refused = why.ok ? floorsAdmit(c, asOf) : why;
    return { code: c.code, suggestable: refused.ok, held: c.held, reason: refused.ok ? null : refused.reason };
  });
}

// --- the checks: one per stored field, read by the guard and the reader ---

type Check = (v: unknown) => string | null;

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function boundText(b: Bound, unit: string): string {
  return b.minExclusive ? `above ${b.min} and at most ${b.max} (${unit})` : `between ${b.min} and ${b.max} (${unit})`;
}

const FRACTION = 'a fraction: 0.10 is 10%';

function inBound(n: number, b: Bound): boolean {
  return (b.minExclusive ? n > b.min : n >= b.min) && n <= b.max;
}

function num(b: Bound, unit: string, opts: { nullable?: boolean; integer?: boolean } = {}): Check {
  return (v) => {
    if (v === null && opts.nullable) return null;
    if (typeof v !== 'number' || !Number.isFinite(v)) return opts.nullable ? 'must be a number or null' : 'must be a number';
    if (opts.integer && !Number.isInteger(v)) return 'must be a whole number';
    return inBound(v, b) ? null : `must be ${boundText(b, unit)}`;
  };
}

const bool: Check = (v) => (typeof v === 'boolean' ? null : 'must be true or false');

const MODEL_ID = /^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/;

function tickerList(max: number): Check {
  return (v) => {
    if (!Array.isArray(v)) return 'must be a list of tickers';
    if (v.length > max) return `must hold at most ${max} tickers`;
    const seen = new Set<string>();
    for (const t of v) {
      if (typeof t !== 'string' || t.trim() === '' || t.length > MODEL_TICKER_MAX_LEN) {
        return `must hold tickers of 1 to ${MODEL_TICKER_MAX_LEN} characters (got ${JSON.stringify(t)})`;
      }
      if (seen.has(t)) return `names ${t} twice`;
      seen.add(t);
    }
    return null;
  };
}

function codeList(pattern: RegExp, what: string, opts: { nullable: boolean; max: number }): Check {
  return (v) => {
    if (v === null && opts.nullable) return null;
    if (!Array.isArray(v)) return opts.nullable ? `must be a list of ${what} or null (any)` : `must be a list of ${what}`;
    if (opts.nullable && v.length === 0) return 'must not be empty (null admits any)';
    if (v.length > opts.max) return `must hold at most ${opts.max} entries`;
    const seen = new Set<string>();
    for (const c of v) {
      if (typeof c !== 'string' || !pattern.test(c)) return `must hold ${what} (got ${JSON.stringify(c)})`;
      if (seen.has(c)) return `names ${c} twice`;
      seen.add(c);
    }
    return null;
  };
}

const ALPHA2 = /^[A-Z]{2}$/;
const ALPHA3 = /^[A-Z]{3}$/;
const EXCHANGE = /^[A-Z0-9]{1,12}$/;

const checkModel: Check = (v) => {
  if (v === null) return null;
  if (!isObject(v)) return 'must be { id, version } or null';
  const extra = Object.keys(v).filter((k) => k !== 'id' && k !== 'version');
  if (extra.length) return `has an unknown field ${extra[0]}`;
  if (typeof v.id !== 'string' || !MODEL_ID.test(v.id)) return '.id must be 1 to 64 letters, digits, dots, dashes or underscores';
  const why = num(MODEL_BOUNDS.modelVersion, 'a whole number', { integer: true })(v.version);
  return why ? `.version ${why}` : null;
};

const checkAim: Check = (v) => {
  if (!isObject(v)) return 'must be { kind, value }';
  const extra = Object.keys(v).filter((k) => k !== 'kind' && k !== 'value');
  if (extra.length) return `has an unknown field ${extra[0]}`;
  if (!(AIM_KINDS as readonly unknown[]).includes(v.kind)) return `.kind must be one of ${AIM_KINDS.join(', ')}`;
  const value = v.value ?? null;
  if (v.kind === 'target_vol' || v.kind === 'target_return') {
    if (value === null) return `.value is needed for ${v.kind}`;
    const b = v.kind === 'target_vol' ? MODEL_BOUNDS.targetVol : MODEL_BOUNDS.targetReturn;
    const why = num(b, FRACTION)(value);
    return why ? `.value ${why}` : null;
  }
  return value === null ? null : `.value is only for target_vol and target_return, not ${v.kind}`;
};

const checkPreset: Check = (v) => {
  if (v === null) return null;
  if (!isObject(v)) return 'must be { id, version } or null';
  if (typeof v.id !== 'string' || typeof v.version !== 'number') return 'must be { id, version } or null';
  return findPreset(v.id, v.version) ? null : `names no preset (${v.id} v${String(v.version)})`;
};

const UNIVERSE_FIELDS: Record<keyof UniverseFilter, Check> = {
  preset: checkPreset,
  countries: codeList(ALPHA2, 'two-letter country codes', { nullable: true, max: MAX_UNIVERSE_LIST }),
  exchanges: codeList(EXCHANGE, 'exchange codes', { nullable: true, max: MAX_UNIVERSE_LIST }),
  domiciles: codeList(ALPHA2, 'two-letter country codes', { nullable: true, max: MAX_UNIVERSE_LIST }),
  fundDomicilesExcluded: codeList(ALPHA2, 'two-letter country codes', { nullable: false, max: MAX_UNIVERSE_LIST }),
  types: (v) => {
    if (v === null) return null;
    if (!Array.isArray(v) || v.length === 0) return 'must be a non-empty list of types or null (any)';
    for (const t of v) if (!(INSTRUMENT_TYPES as readonly unknown[]).includes(t)) return `must hold types from ${INSTRUMENT_TYPES.join(', ')} (got ${JSON.stringify(t)})`;
    return new Set(v).size === v.length ? null : 'names a type twice';
  },
  currencies: codeList(ALPHA3, 'three-letter currency codes', { nullable: true, max: MAX_UNIVERSE_LIST }),
  ucits: (v) => (v === 'any' || v === 'required' ? null : "must be 'any' or 'required'"),
  availableFunds: (v) => (v === null ? null : tickerList(MAX_AVAILABLE_FUNDS)(v) ?? (Array.isArray(v) && v.length === 0 ? 'must not be empty (null admits any)' : null)),
};

const checkUniverse: Check = (v) => {
  if (v === null) return null;
  if (!isObject(v)) return 'must be a filter object or null';
  for (const k of Object.keys(v)) if (!(k in UNIVERSE_FIELDS)) return `has an unknown field ${k}`;
  for (const [k, check] of Object.entries(UNIVERSE_FIELDS)) {
    if (!(k in v)) return `.${k} is missing`;
    const why = check(v[k]);
    if (why) return `.${k} ${why}`;
  }
  return null;
};

/** A section whose leaves merge one by one across the layers. */
type Section = Record<string, Check>;

const SECTIONS: Record<string, Section> = {
  limits: {
    volCap: num(MODEL_BOUNDS.volCap, FRACTION, { nullable: true }),
    returnFloor: num(MODEL_BOUNDS.returnFloor, FRACTION, { nullable: true }),
    maxDrawdown: num(MODEL_BOUNDS.maxDrawdown, FRACTION, { nullable: true }),
    maxTurnover: num(MODEL_BOUNDS.maxTurnover, FRACTION, { nullable: true }),
  },
  suggestions: { stocks: bool, funds: bool, alpha: bool },
  crypto: { allowed: bool, expectedReturn: num(MODEL_BOUNDS.cryptoExpectedReturn, FRACTION, { nullable: true }) },
  groupMax: { equity: num(MODEL_BOUNDS.groupMax, FRACTION), bonds: num(MODEL_BOUNDS.groupMax, FRACTION) },
  costs: {
    dealingCharge: num(MODEL_BOUNDS.dealingCharge, 'money a trade, in the base currency'),
    fxFee: num(MODEL_BOUNDS.fxFee, FRACTION),
    platformFee: num(MODEL_BOUNDS.platformFee, `${FRACTION} a year`),
  },
  triggers: {
    returnGap: num(MODEL_BOUNDS.returnGap, `${FRACTION} a year`),
    confidence: num(MODEL_BOUNDS.confidence, FRACTION),
    volGap: num(MODEL_BOUNDS.volGap, `${FRACTION} a year`),
    minNetGain: num(MODEL_BOUNDS.minNetGain, `${FRACTION} of the book's value`),
    cooldownDays: num(MODEL_BOUNDS.cooldownDays, 'whole days', { integer: true }),
    aeaFlag: bool,
  },
  review: {
    frequency: (v) => ((REVIEW_FREQUENCIES as readonly unknown[]).includes(v) ? null : `must be one of ${REVIEW_FREQUENCIES.join(', ')}`),
    day: num(MODEL_BOUNDS.reviewDay, 'a day: 1-7 (Monday first) weekly, the day of the month otherwise', { nullable: true, integer: true }),
  },
};

/** The fields set whole: a layer's value replaces the one below it. */
const WHOLE: Record<string, Check> = {
  model: checkModel,
  aim: checkAim,
  frozen: tickerList(MAX_HELD_LIST),
  noSell: tickerList(MAX_HELD_LIST),
  newHoldings: bool,
  universe: checkUniverse,
};

/** A field's path joined to its reason: ".limits.volCap must be ...", ".model.id must be ...". */
function at(path: string, why: string): string {
  return `${path}${why.startsWith('.') ? '' : ' '}${why}`;
}

/**
 * Why one stored block is refused, or null, as a path from the block and its
 * reason (".limits.volCap must be ...", or " has an unknown field x"). A
 * block is partial: any field may be absent, and a present one must be inside
 * its bound. An unknown field is refused too, so a misspelt one is never
 * saved and silently ignored.
 */
export function modelBlockProblem(block: unknown): string | null {
  if (!isObject(block)) return ' must be an object';
  for (const [k, v] of Object.entries(block)) {
    if (k in WHOLE) {
      const why = WHOLE[k](v);
      if (why) return at(`.${k}`, why);
    } else if (k in SECTIONS) {
      if (!isObject(v)) return `.${k} must be an object`;
      for (const [leaf, lv] of Object.entries(v)) {
        const check = SECTIONS[k][leaf];
        if (!check) return `.${k} has an unknown field ${leaf}`;
        const why = check(lv);
        if (why) return at(`.${k}.${leaf}`, why);
      }
    } else {
      return ` has an unknown field ${k}`;
    }
  }
  const frozen = Array.isArray(block.frozen) ? block.frozen : [];
  const both = (Array.isArray(block.noSell) ? block.noSell : []).find((t) => frozen.includes(t));
  if (both !== undefined) return `.noSell names ${String(both)}, which is frozen (a frozen holding is never sold already)`;
  if (isObject(block.review) && block.review.frequency === 'weekly' && typeof block.review.day === 'number' && block.review.day > 7) {
    return `.review.day must be 1-7 (Monday first) for a weekly review (got ${block.review.day})`;
  }
  return null;
}

/**
 * Why a settings blob's model settings are refused, or null: the person's
 * default and every portfolio's own block. Absent is fine (it reads as the
 * defaults). Read by the edge before a settings write.
 */
export function modelSettingsProblem(settings: unknown): string | null {
  if (!isObject(settings)) return null;
  if (settings.modelDefaults !== undefined) {
    const why = modelBlockProblem(settings.modelDefaults);
    if (why) return `modelDefaults${why}`;
  }
  if (Array.isArray(settings.portfolios)) {
    for (let i = 0; i < settings.portfolios.length; i++) {
      const p = settings.portfolios[i];
      if (!isObject(p) || p.modelSettings === undefined) continue;
      const why = modelBlockProblem(p.modelSettings);
      if (why) return `portfolios[${i}].modelSettings${why}`;
    }
  }
  return null;
}

/** Where a resolved field came from. */
export type ModelSettingSource = 'built-in' | 'person' | 'portfolio';

export interface ResolvedModelSettings {
  settings: ModelSettings;
  /** Each field's source, by path ('aim', 'limits.volCap'). */
  from: Record<string, ModelSettingSource>;
  /** Stored fields left out because they break their bound, each with its reason. */
  ignored: string[];
}

/**
 * The settings a portfolio's model reads: its own value of each field, else
 * the person's default, else the built-in default. A section's leaves merge
 * one by one; a whole field (the model, the aim, the lists, the filter) is
 * taken whole. A stored field that breaks its bound is left out and named in
 * `ignored`, never read. D21's three bars fall back to the portfolio's drift
 * bars (`rebalanceThresholds`, item 45) before the person's default, so the
 * bars the owner already set for the drift check carry into the model check.
 */
export function resolveModelSettings(settings: unknown, portfolioId: string): ResolvedModelSettings {
  const s = isObject(settings) ? settings : {};
  const portfolio = Array.isArray(s.portfolios)
    ? s.portfolios.find((p): p is Record<string, unknown> => isObject(p) && p.id === portfolioId)
    : undefined;
  const drift = isObject(portfolio?.rebalanceThresholds) ? portfolio.rebalanceThresholds : {};
  const driftLayer = {
    triggers: Object.fromEntries(([
      ['returnGap', drift.returnThreshold], ['confidence', drift.confidence], ['volGap', drift.riskThreshold],
    ] as const).filter(([, v]) => v !== undefined)),
  };
  const layers: Array<[ModelSettingSource, string, unknown]> = [
    ['person', 'modelDefaults', s.modelDefaults],
    ['portfolio', `portfolio ${portfolioId}'s drift bars`, driftLayer],
    ['portfolio', `portfolio ${portfolioId}`, portfolio?.modelSettings],
  ];
  const out = structuredClone(MODEL_SETTINGS_DEFAULTS) as ModelSettings;
  const target = out as unknown as Record<string, unknown>;
  const from: Record<string, ModelSettingSource> = {};
  const ignored: string[] = [];
  for (const k of Object.keys(WHOLE)) from[k] = 'built-in';
  for (const [k, sec] of Object.entries(SECTIONS)) for (const leaf of Object.keys(sec)) from[`${k}.${leaf}`] = 'built-in';
  for (const [source, name, block] of layers) {
    if (block === undefined) continue;
    if (!isObject(block)) { ignored.push(`${name}: must be an object`); continue; }
    for (const [k, v] of Object.entries(block)) {
      if (k in WHOLE) {
        const why = WHOLE[k](v);
        if (why) { ignored.push(`${name}.${k}: ${why}`); continue; }
        target[k] = structuredClone(v);
        from[k] = source;
      } else if (k in SECTIONS) {
        if (!isObject(v)) { ignored.push(`${name}.${k}: must be an object`); continue; }
        for (const [leaf, lv] of Object.entries(v)) {
          const check = SECTIONS[k][leaf];
          const why = check ? check(lv) : 'is not a field';
          if (why) { ignored.push(`${name}.${k}.${leaf}: ${why}`); continue; }
          (target[k] as Record<string, unknown>)[leaf] = lv;
          from[`${k}.${leaf}`] = source;
        }
      } else {
        ignored.push(`${name}.${k}: is not a field`);
      }
    }
  }
  const frozen = new Set(out.frozen);
  const clash = out.noSell.filter((t) => frozen.has(t));
  if (clash.length) {
    out.noSell = out.noSell.filter((t) => !frozen.has(t));
    ignored.push(`noSell: ${clash.join(', ')} frozen, so left out of no-sell`);
  }
  return { settings: out, from, ignored };
}

function canonical(v: unknown): string {
  if (Array.isArray(v)) return `[${v.map(canonical).join(',')}]`;
  if (isObject(v)) return `{${Object.keys(v).sort().map((k) => `${JSON.stringify(k)}:${canonical(v[k])}`).join(',')}}`;
  return JSON.stringify(v);
}

function deepFreeze<T>(v: T): T {
  if (v && typeof v === 'object') {
    for (const x of Object.values(v as object)) deepFreeze(x);
    Object.freeze(v);
  }
  return v;
}
